import { useState, useEffect } from "react";
import { useAuth, API } from "../../App";
import axios from "axios";
import { toast } from "sonner";
import { 
  Users, Pencil, Ban, CheckCircle, Shield, ShieldOff, 
  Loader2, Search, Image, Trash2, Mail, User
} from "lucide-react";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../ui/dialog";
import { Avatar, AvatarFallback, AvatarImage } from "../ui/avatar";

const UsersAdmin = () => {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingUser, setEditingUser] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionLoading, setActionLoading] = useState({});

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/admin/users`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUsers(response.data);
    } catch (error) {
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const toggleAdmin = async (userId) => {
    setActionLoading(prev => ({...prev, [userId]: 'admin'}));
    try {
      await axios.put(`${API}/admin/users/${userId}/toggle-admin`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Admin status updated");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to update admin status");
    } finally {
      setActionLoading(prev => ({...prev, [userId]: null}));
    }
  };

  const banUser = async (userId) => {
    if (!confirm("Are you sure you want to ban this user? This will unpublish all their content.")) return;
    setActionLoading(prev => ({...prev, [userId]: 'ban'}));
    try {
      await axios.post(`${API}/admin/users/${userId}/ban`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User banned successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to ban user");
    } finally {
      setActionLoading(prev => ({...prev, [userId]: null}));
    }
  };

  const unbanUser = async (userId) => {
    setActionLoading(prev => ({...prev, [userId]: 'unban'}));
    try {
      await axios.post(`${API}/admin/users/${userId}/unban`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User unbanned successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to unban user");
    } finally {
      setActionLoading(prev => ({...prev, [userId]: null}));
    }
  };

  const updateUser = async () => {
    try {
      await axios.put(`${API}/admin/users/${editingUser.id}`, {
        full_name: editingUser.full_name,
        email: editingUser.email
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("User updated successfully");
      setDialogOpen(false);
      setEditingUser(null);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to update user");
    }
  };

  const deleteProfileImage = async (userId) => {
    if (!confirm("Are you sure you want to delete this user's profile image?")) return;
    setActionLoading(prev => ({...prev, [userId]: 'image'}));
    try {
      await axios.delete(`${API}/admin/users/${userId}/profile-image`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Profile image deleted");
      fetchUsers();
    } catch (error) {
      toast.error("Failed to delete profile image");
    } finally {
      setActionLoading(prev => ({...prev, [userId]: null}));
    }
  };

  const filteredUsers = users.filter(u => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      u.full_name?.toLowerCase().includes(query) ||
      u.email?.toLowerCase().includes(query)
    );
  });

  const getInitials = (name) => {
    if (!name) return "?";
    return name.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2);
  };

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-[#7C2C3B]" /></div>;
  }

  return (
    <div data-testid="admin-users">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Barlow Condensed' }}>
          <Users className="w-8 h-8 inline mr-2 text-[#7C2C3B]" />
          USER MANAGEMENT
        </h1>
        <div className="text-sm text-slate-500">
          {users.length} total users | {users.filter(u => u.is_banned).length} banned
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input
          placeholder="Search by name or email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pl-10"
          data-testid="user-search"
        />
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-[#0F172A]">{users.length}</div>
          <div className="text-sm text-slate-500">Total Users</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-green-600">{users.filter(u => u.is_subscribed).length}</div>
          <div className="text-sm text-slate-500">Subscribers</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-blue-600">{users.filter(u => u.is_admin).length}</div>
          <div className="text-sm text-slate-500">Admins</div>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-200">
          <div className="text-2xl font-bold text-red-600">{users.filter(u => u.is_banned).length}</div>
          <div className="text-sm text-slate-500">Banned</div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <div className="overflow-x-auto max-h-[500px]">
          <Table>
            <TableHeader className="sticky top-0 bg-slate-100 z-10">
              <TableRow>
                <TableHead className="w-16">Avatar</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Email</TableHead>
                <TableHead className="w-24">Status</TableHead>
                <TableHead className="w-24">Role</TableHead>
                <TableHead className="w-24">Subscribed</TableHead>
                <TableHead className="w-28">Joined</TableHead>
                <TableHead className="w-48">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredUsers.map(user => (
                <TableRow 
                  key={user.id} 
                  className={user.is_banned ? "bg-red-50" : ""}
                  data-testid={`user-row-${user.id}`}
                >
                  <TableCell>
                    <Avatar className="h-10 w-10">
                      <AvatarImage src={user.profile_image_url} alt={user.full_name} />
                      <AvatarFallback className="bg-[#7C2C3B] text-white text-sm">
                        {getInitials(user.full_name)}
                      </AvatarFallback>
                    </Avatar>
                  </TableCell>
                  <TableCell className="font-medium">{user.full_name}</TableCell>
                  <TableCell className="text-slate-600">{user.email}</TableCell>
                  <TableCell>
                    {user.is_banned ? (
                      <span className="flex items-center gap-1 text-red-600 text-xs font-medium">
                        <Ban className="w-3 h-3" /> Banned
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-green-600 text-xs font-medium">
                        <CheckCircle className="w-3 h-3" /> Active
                      </span>
                    )}
                  </TableCell>
                  <TableCell>
                    {user.is_admin ? (
                      <span className="flex items-center gap-1 text-blue-600 text-xs font-medium">
                        <Shield className="w-3 h-3" /> Admin
                      </span>
                    ) : (
                      <span className="text-slate-400 text-xs">User</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <span className={`text-xs px-2 py-1 rounded ${user.is_subscribed ? "bg-green-100 text-green-700" : "bg-slate-100 text-slate-600"}`}>
                      {user.is_subscribed ? "Yes" : "No"}
                    </span>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-slate-500" data-testid={`user-joined-${user.id}`}>
                      {user.created_at ? new Date(user.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '-'}
                    </span>
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1 flex-wrap">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2"
                        onClick={() => { setEditingUser({...user}); setDialogOpen(true); }}
                        data-testid={`edit-user-${user.id}`}
                      >
                        <Pencil className="w-3 h-3 mr-1" /> Edit
                      </Button>
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 px-2"
                        onClick={() => toggleAdmin(user.id)}
                        disabled={actionLoading[user.id] === 'admin'}
                        data-testid={`toggle-admin-${user.id}`}
                      >
                        {actionLoading[user.id] === 'admin' ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : user.is_admin ? (
                          <><ShieldOff className="w-3 h-3 mr-1" /> Demote</>
                        ) : (
                          <><Shield className="w-3 h-3 mr-1" /> Promote</>
                        )}
                      </Button>
                      {user.is_banned ? (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-green-600 border-green-300 hover:bg-green-50"
                          onClick={() => unbanUser(user.id)}
                          disabled={actionLoading[user.id] === 'unban'}
                          data-testid={`unban-user-${user.id}`}
                        >
                          {actionLoading[user.id] === 'unban' ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <><CheckCircle className="w-3 h-3 mr-1" /> Unban</>
                          )}
                        </Button>
                      ) : (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 px-2 text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => banUser(user.id)}
                          disabled={actionLoading[user.id] === 'ban'}
                          data-testid={`ban-user-${user.id}`}
                        >
                          {actionLoading[user.id] === 'ban' ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <><Ban className="w-3 h-3 mr-1" /> Ban</>
                          )}
                        </Button>
                      )}
                      {user.profile_image_url && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 w-7 p-0 text-red-600 border-red-300 hover:bg-red-50"
                          onClick={() => deleteProfileImage(user.id)}
                          disabled={actionLoading[user.id] === 'image'}
                          title="Delete profile image"
                          data-testid={`delete-image-${user.id}`}
                        >
                          {actionLoading[user.id] === 'image' ? (
                            <Loader2 className="w-3 h-3 animate-spin" />
                          ) : (
                            <Trash2 className="w-3 h-3" />
                          )}
                        </Button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      </div>

      <p className="text-xs text-slate-500 mt-4 text-center">
        Showing {filteredUsers.length} of {users.length} users
      </p>

      {/* Edit User Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>EDIT USER</DialogTitle>
          </DialogHeader>
          
          {editingUser && (
            <div className="grid gap-4 py-4">
              <div className="flex justify-center">
                <Avatar className="h-20 w-20">
                  <AvatarImage src={editingUser.profile_image_url} alt={editingUser.full_name} />
                  <AvatarFallback className="bg-[#7C2C3B] text-white text-2xl">
                    {getInitials(editingUser.full_name)}
                  </AvatarFallback>
                </Avatar>
              </div>
              <div>
                <Label className="flex items-center gap-2"><User className="w-4 h-4" /> Full Name</Label>
                <Input 
                  value={editingUser.full_name} 
                  onChange={(e) => setEditingUser({...editingUser, full_name: e.target.value})}
                />
              </div>
              <div>
                <Label className="flex items-center gap-2"><Mail className="w-4 h-4" /> Email</Label>
                <Input 
                  type="email"
                  value={editingUser.email} 
                  onChange={(e) => setEditingUser({...editingUser, email: e.target.value})}
                />
              </div>
              <div className="text-sm text-slate-500 bg-slate-50 p-3 rounded">
                <p><strong>Joined:</strong> {new Date(editingUser.created_at).toLocaleDateString()}</p>
                <p><strong>Subscribed:</strong> {editingUser.is_subscribed ? "Yes" : "No"}</p>
                <p><strong>Admin:</strong> {editingUser.is_admin ? "Yes" : "No"}</p>
                <p><strong>Banned:</strong> {editingUser.is_banned ? "Yes" : "No"}</p>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button onClick={updateUser} className="bg-[#7C2C3B] hover:bg-[#63232F]" data-testid="save-user-btn">
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UsersAdmin;
